import { useState, useRef } from 'react';
import { Download, Upload, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { dataStore } from '@/lib/store';

export default function BackupRestore() {
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleBackup = () => {
    try {
      const data = dataStore.exportData();
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-festival-escalada-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage('Backup realizado com sucesso!');
    } catch (error) {
      setMessage('Erro ao criar backup.');
    }
  };

  const handleRestore = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setMessage('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = e.target?.result as string;
        dataStore.importData(jsonData);
        setMessage('Dados restaurados com sucesso! Recarregue a página para ver as alterações.');
      } catch (error) {
        setMessage('Erro ao restaurar dados. Verifique se o arquivo é válido.');
      } finally {
        setIsProcessing(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.readAsText(file);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          Backup e Restauração
        </CardTitle>
        <CardDescription>
          Faça backup dos dados ou restaure de um backup anterior
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex space-x-2">
            <Button onClick={handleBackup} variant="outline" className="flex-1">
              <Download className="w-4 h-4 mr-2" />
              Fazer Backup
            </Button>
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleRestore}
                disabled={isProcessing}
                className="hidden"
                id="restore-input"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                variant="outline"
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                {isProcessing ? 'Processando...' : 'Restaurar Backup'}
              </Button>
            </div>
          </div>

          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          <div className="text-xs text-gray-500">
            <p><strong>Backup:</strong> Salva todos os dados (atletas, pontuações, tentativas) em um arquivo JSON.</p>
            <p><strong>Restaurar:</strong> Carrega dados de um arquivo de backup. Substitui todos os dados atuais.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
